// useConfirmDialog.tsx
import { useState, useCallback, useRef, useMemo } from 'react';
import { Dialog, DialogContent, IconButton, Typography, Button, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

interface ConfirmDialogOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

export const useConfirmDialog = () => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions | null>(null);
  const resolver = useRef<(value: boolean) => void>();

  const confirm = useCallback((opts: ConfirmDialogOptions): Promise<boolean> => {
    setOptions(opts);
    setOpen(true);
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const handleClose = useCallback((result: boolean) => {
    setOpen(false);
    resolver.current?.(result);
  }, []);

  // IMPORTANT: this is a memoized JSX *element*, not a component function.
  // Rendering it as {ConfirmDialog} in the parent works directly —
  // no <ConfirmDialog /> invocation needed, and it won't remount on
  // every parent render the way an inline component definition would.
  const ConfirmDialog = useMemo(
    () => (
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogContent sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
          <IconButton
            onClick={() => handleClose(false)}
            sx={{ position: 'absolute', top: 12, right: 12 }}>
            <CloseIcon />
          </IconButton>

          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: '#4DB6AC',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}>
            <CheckIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>

          <Typography variant="h6" fontWeight={600} mb={1}>
            {options?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {options?.description}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={() => handleClose(true)}
            sx={{ mb: 1.5, py: 1.25, textTransform: 'none', bgcolor: 'primary.main' }}>
            {options?.confirmLabel ?? 'Confirm'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleClose(false)}
            sx={{ py: 1.25, textTransform: 'none', color: 'primary.main' }}>
            {options?.cancelLabel ?? 'Cancel'}
          </Button>
        </DialogContent>
      </Dialog>
    ),
    [open, options]
  );

  return { confirm, ConfirmDialog };
};
